import React, { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { get, queue, uid, pending } from "./techData";
import { Card } from "./ui";
import { money, pendingTotal } from "./LabourCard";

const ROW = { labour: "bg-[#eaf3ff]", supplier_freight: "bg-[#fffbdd]", manual: "bg-[#f5f5f5]" };

function Booking({ rem, onSave, onPrompt }) {
  const [team, setTeam] = useState(() => JSON.parse(localStorage.getItem("tc:/team") || "[]"));
  useEffect(() => { get("/team").then(setTeam).catch(() => {}); }, []);
  if (!rem) return <button onClick={() => onPrompt?.("rem")} data-testid="tech-rem-create" className="w-full h-14 rounded-2xl bg-white border-2 border-dashed border-slate-300 text-[18px] font-black text-slate-600">No Reminder - Create One</button>;
  const names = [...new Set([...team.map((x) => x.name).filter(Boolean), rem.who].filter(Boolean))];
  return ( // the reminder (White Board magnet) — the work order itself is never re-booked
    <div className="grid grid-cols-[1fr_1.25fr_auto] gap-1.5 items-center" data-testid="tech-rem-booking">
      <input type="date" value={rem.date || ""} onChange={(e) => e.target.value && onSave(rem, { due_date: e.target.value })} data-testid="tech-rem-date"
        className="w-full h-12 rounded-xl border-2 border-slate-200 bg-slate-100 px-2 text-[16px] font-black text-[#0f2f52]" />
      <select value={rem.who || ""} onChange={(e) => onSave(rem, { assigned_to: e.target.value })} data-testid="tech-rem-tech"
        className="w-full h-12 rounded-xl border-2 border-slate-200 px-1 text-[15px] font-black bg-white text-[#0f2f52]">
        <option value="">Unassigned</option>
        {names.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <button onClick={() => onSave(rem, { on_board: !rem.on_board })} data-testid="tech-rem-board" className="flex flex-col items-center text-[11px] font-black text-slate-600 leading-tight">
        <span className={`h-8 w-8 rounded-lg border-[3px] flex items-center justify-center text-[16px] ${rem.on_board ? "bg-green-600 border-green-600 text-white" : "border-slate-300 bg-white"}`}>{rem.on_board && "✓"}</span>White<br />Board
      </button>
    </div>
  );
}

function QtyInput({ l, onSet, testid }) {
  const [t, setT] = useState(String(Number(l.qty) || 0));
  const commit = () => {
    const q = Math.round(parseFloat(t) * 100) / 100;
    if (!(q > 0)) { window.alert("Enter a quantity above 0, or use the bin to remove the line."); setT(String(Number(l.qty) || 0)); return; }
    if (q !== Number(l.qty)) onSet(q);
  };
  return <input value={t} onChange={(e) => setT(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} onFocus={(e) => e.target.select()}
    inputMode="decimal" data-testid={testid} className="w-12 h-9 rounded-lg border-2 border-slate-300 bg-white text-center text-[15px] font-black" />;
}

function Box({ wo, field, label, bg, testid }) {
  const [t, setT] = useState(wo[field] || ""), typing = useRef(false);
  useEffect(() => { if (!typing.current) setT(wo[field] || ""); }, [wo.id, wo[field]]); // eslint-disable-line
  const save = () => {
    typing.current = false;
    if (t === (wo[field] || "")) return;
    wo[field] = t;
    queue(`wo:${wo.id}:${field}`, "PATCH", `/tech/workorder/${wo.id}`, { [field]: t });
  };
  return (
    <div className="rounded-xl overflow-hidden border border-slate-300">
      <div className="px-3 py-1 text-[12px] font-black text-slate-600 bg-white/70">{label}</div>
      <textarea value={t} onChange={(e) => setT(e.target.value)} onFocus={() => { typing.current = true; }} onBlur={save} rows={4} data-testid={testid}
        className="w-full p-3 text-[17px] leading-snug outline-none" style={{ background: bg }} />
    </div>
  );
}

// the open work order(s) for this client: pink (issue) + green (result) boxes, then its lines
export default function WoCard({ clientId, job, v, setV, onPrompt, tiles }) {
  const path = `/tech/client/${clientId}/workorders`;
  const [wos, setWos] = useState(() => JSON.parse(localStorage.getItem(`tc:${path}`) || "[]"));
  const [remPatch, setRemPatch] = useState({});
  useEffect(() => {
    const local = () => setWos(JSON.parse(localStorage.getItem(`tc:${path}`) || "[]"));
    const pull = () => pending().then((n) => { if (!n && navigator.onLine) get(path).then(setWos).catch(() => {}); }).catch(() => {});
    pull(); window.addEventListener("tech:wo", local);
    const t = setInterval(pull, 8000); // office changes show on the phone too
    return () => { clearInterval(t); window.removeEventListener("tech:wo", local); };
  }, [path]);
  const wo = wos.find((w) => w.id === v.wo_id) || wos.find((w) => w.id === job?.wo_id) || wos[0];
  useEffect(() => { if (wo && v.status !== "done" && (v.wo_id !== wo.id || v.wo_number !== wo.wo_number)) setV({ wo_id: wo.id, wo_number: wo.wo_number }); }, [wo?.id]); // eslint-disable-line
  const base = (job?.id && !job.visit_id ? { id: job.id, date: job.date, who: job.tech || "", on_board: job.on_board !== false } : null) || wo?.reminder;
  const rem = base && { ...base, ...(remPatch.id === base.id ? remPatch : {}) };
  const saveRem = (r, patch) => { // straight onto the office reminder / White Board magnet
    const q = Object.entries(patch).map(([k, x]) => `${k}=${encodeURIComponent(x)}`).join("&");
    queue(`rem:${r.id}:${Object.keys(patch)[0]}`, "PATCH", `/reminders/${r.id}?${q}`, {});
    const map = { due_date: "date", assigned_to: "who", on_board: "on_board" };
    setRemPatch((p) => ({ ...(p.id === r.id ? p : {}), id: r.id, ...Object.fromEntries(Object.entries(patch).map(([k, x]) => [map[k], x])) }));
  };
  const dashed = "w-full h-14 rounded-2xl bg-white border-2 border-dashed border-slate-300 text-[18px] font-black text-slate-600";
  const woBtn = !wo && !(v.status === "done" && v.wo_number) && <button onClick={() => onPrompt?.("wo")} data-testid="tech-wo-create" className={dashed}>No Work Order - Create One</button>;
  const head = <><Booking rem={rem} onSave={saveRem} onPrompt={onPrompt} />{woBtn}{tiles}</>;
  if (v.status === "done" && v.wo_number && wo?.id !== v.wo_id) return <>{head}<Card className="text-[16px] font-black text-[#0f2f52]" data-testid="tech-wo-used">Labour & parts went on W/O {v.wo_number}</Card></>;
  if (!wo) return head;
  const lines = wo.lines?.length ? wo.lines : [...(wo.parts || []).map((p) => ({ kind: "part", item: p.name, unit: p.unit_price, qty: p.qty, net: p.qty * p.unit_price })), ...(wo.labour || []).map((l) => ({ kind: "labour", item: l.description, unit: l.rate, qty: l.hours, net: l.hours * l.rate }))];
  const adding = pendingTotal(v);
  const setQty = (i, l, q) => {
    const ref = uid();
    queue(`woqty:${ref}`, "POST", `/tech/workorder/${wo.id}/lines/qty`, { ref, index: i, item: l.item || "", qty: q });
    const nl = lines.map((x, n) => (n === i ? { ...x, qty: q, gross: q * (x.unit || 0), net: q * (x.unit || 0) - (x.discount || 0) } : x));
    const next = wos.map((w) => (w.id === wo.id ? { ...w, lines: nl, total: nl.reduce((a, x) => a + (x.net || 0), 0) } : w));
    localStorage.setItem(`tc:${path}`, JSON.stringify(next));
    setWos(next);
  };
  const del = (i, l) => {
    if (!window.confirm(`Remove line "${l.item || l.kind}"?`)) return;
    const ref = uid();
    queue(`wodel:${ref}`, "POST", `/tech/workorder/${wo.id}/lines/remove`, { ref, index: i, item: l.item || "" });
    const nl = lines.filter((_, n) => n !== i), next = wos.map((w) => (w.id === wo.id ? { ...w, lines: nl, total: nl.reduce((a, x) => a + (x.net || 0), 0) } : w));
    localStorage.setItem(`tc:${path}`, JSON.stringify(next));
    setWos(next);
  };
  return (<>{head}
    <Card className="space-y-3" data-testid="tech-wo-card">
      {wos.length > 1 && v.status !== "done" && (
        <label className="block">
          <span className="text-[13px] font-black text-slate-500">Add today's labour & parts to</span>
          <select value={wo.id} onChange={(e) => { const w = wos.find((x) => x.id === e.target.value); setV({ wo_id: w.id, wo_number: w.wo_number }); }} data-testid="tech-wo-pick"
            className="w-full h-14 rounded-xl border-2 border-slate-200 px-3 text-[17px] font-bold bg-white">
            {wos.map((w) => <option key={w.id} value={w.id}>W/O {w.wo_number} · {(w.issue_text || w.description || w.type || "").slice(0, 40)}</option>)}
          </select>
        </label>
      )}
        {!wo.issue_text && wo.description && <div className="text-[16px] text-slate-800">{wo.description}</div>}
        <Box wo={wo} field="issue_text" label="ISSUE — on Work Order, FM84 & Quote" bg="#ffc5c5" testid="tech-wo-issue" />
        <Box wo={wo} field="result_text" label="RESULT — on Work Order & Invoice" bg="#ccffcc" testid="tech-wo-result" />
        <div className="space-y-1" data-testid="tech-wo-lines">
          <div className="text-[13px] font-black text-slate-500">ON THIS WORK ORDER · W/O {wo.wo_number}</div>
          {lines.length === 0 ? <div className="text-[15px] text-slate-400">No lines yet.</div> : (
            <table className="w-full text-[14px] border border-slate-200">
              <thead className="bg-[#eef3f9] text-[#123a63] text-[12px]"><tr><th className="px-1.5 py-1 text-left">Item</th><th className="px-1 py-1 text-right">Unit</th><th className="px-1 py-1 text-center">Qty</th><th className="px-1.5 py-1 text-right">Net</th><th className="w-9" /></tr></thead>
              <tbody>{lines.map((l, i) => (
                <tr key={i} className={`border-t border-slate-100 ${ROW[l.kind] || "bg-white"}`} data-testid="tech-wo-line">
                  <td className="px-1.5 py-1 text-slate-800 leading-tight">{l.kind === "labour" && <span className="text-[10px] font-black px-1 mr-1 rounded bg-black/10">LAB</span>}{l.item}</td>
                  <td className="px-1 py-1 text-right text-slate-600 whitespace-nowrap">{money(l.unit)}</td>
                  <td className="px-1 py-1 text-center">{v.status !== "done" ? <QtyInput key={`${wo.id}-${i}-${l.qty}`} l={l} onSet={(q) => setQty(i, l, q)} testid={`tech-wo-line-qty-${i}`} /> : Number(l.qty) || 0}</td>
                  <td className="px-1.5 py-1 text-right font-bold whitespace-nowrap">{money(l.net)}</td>
                  <td className="py-1 pr-1 text-center">{v.status !== "done" && <button onClick={() => del(i, l)} data-testid={`tech-wo-line-del-${i}`} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 active:bg-rose-50 active:text-rose-600"><Trash2 className="h-5 w-5" /></button>}</td>
                </tr>))}</tbody>
            </table>
          )}
          <div className="flex justify-between text-[16px] font-black text-[#0f2f52] border-t pt-1"><span>W/O total</span><span>{money(wo.total ?? lines.reduce((a, l) => a + (l.net || 0), 0))}</span></div>
          {adding > 0 && v.status !== "done" && <div className="flex justify-between text-[15px] font-black text-amber-700" data-testid="tech-wo-adding"><span>+ today's labour & parts</span><span>{money(adding)}</span></div>}
        </div>
    </Card></>
  );
}
